/**
 * Krok UXV4a bod 5 — POSTUP ZAKAZKY. JEDNA FUNKCE PRO VSECHNA MISTA.
 *
 * Zadani: „procenta + veta ‚co chybí‘ + kroky (Založení, Prověření, Report,
 * Inzerce, Smlouva) — pouzita na Dnesku, v seznamu, kanbanu i detailu."
 * Predloha `docs/design/ux-v4/` § Pravidla 5: „JEDNA funkce pocita procenta
 * i ‚co chybí‘ pro vsechna mista."
 *
 * Tohle je ta funkce. Nestavi vedle `lib/case-phases.ts`, ale NAD NI:
 * mapovani deseti fazi stavoveho automatu na pet kroku zustava tam (jeden
 * zdroj pravdy, ROZHODNUTI UXR3-FAZE), tady k nemu pribyva to, co predloha
 * v4 chce navic — procenta a veta „co chybí".
 *
 * ---------------------------------------------------------------------------
 * ROZHODNUTI UXV4a-PROCENTA — jak se procenta pocitaji
 *
 * `percent = (hotove kroky + rozpracovanost kroku „ted") / 5`.
 *
 * Hotovy krok se pocita cely, krok „ted" jen tim podilem, ktery UMIME ZMERIT
 * (dnes jediny: „Prověření" z poctu overenych oblasti, `gateProgress` v
 * `lib/case-gates.ts`) — jinde nulou. Nic se nedopocitava odhadem: zakazka ve
 * fazi „Smlouva a předání" ma 80 %, protoze ctyri kroky z peti za sebou
 * opravdu ma, ne protoze „to tak vypada".
 *
 * `closed` = 100 %. Zrusena zakazka a zakazka v nezname fazi procenta NEMAJI
 * (0 % a veta, ktera to rekne) — obrazovka nesmi tvrdit postup, ktery nezna.
 * ---------------------------------------------------------------------------
 */

import { t } from '@reos/i18n';
import {
  CASE_PHASE_IDS,
  caseProgress,
  casePhaseSection,
  type CasePhase,
  type CaseProgressInput,
  type CasePhaseState,
} from './case-phases.js';

const cs = { locale: 'cs' } as const;

/** Krok, ke kteremu se vztahuje veta „do inzerce chybí N kroků". */
const LISTING_INDEX = CASE_PHASE_IDS.indexOf('listing') + 1;

export interface CaseProgressStep {
  readonly id: string;
  readonly label: string;
  /**
   * Zkraceny popisek pod prouzkem (predloha v4: „Report", „Smlouva"). NENI to
   * druhy nazev faze — je to tentyz krok, jen do peti sloupcu na 390 px se
   * „Report pro klienta" nevejde a usek na elipsu by nerekl nic. Plny nazev
   * zustava v `label` (hlavicka zakazky, souhrn) a oba jdou z TEHOZ katalogu.
   */
  readonly shortLabel: string;
  readonly state: CasePhaseState;
  /**
   * Krok QAFIX11 bod 1 (nalez V5): sekce detailu, kterou krok zastupuje —
   * `null` = krok nikam nevede a nesmi vypadat jako odkaz. Hodnota je z
   * TEHOZ mapovani (`casePhaseSection`), jake uz rozhoduje o stavu
   * `unavailable`.
   */
  readonly section: string | null;
}

export interface CaseProgressView {
  /** 0–100, zaokrouhleno; sirka prouzku i cislo vedle nej. */
  readonly percent: number;
  /** Hotove cislo k vypsani („40 %") — formatovani na jednom miste. */
  readonly percentLabel: string;
  /**
   * Tentyz udaj CELOU VETOU („Hotovo 40 %") — predloha `Zakazka.dc.html` ho
   * takhle pise v karte postupu detailu. V radku seznamu by slovo „Hotovo"
   * jen zabralo misto (predloha `Main.dc.html` ma jen cislo), proto dve
   * podoby TEHOZ cisla z jedne funkce, ne dve kopie vypoctu.
   */
  readonly percentDoneLabel: string;
  /** Veta „co chybí" (predloha: „do inzerce chybí 3 kroky"). */
  readonly missing: string;
  /**
   * Radek do seznamu: krok „ted" + co chybi („Prověření · do inzerce chybí
   * 3 kroky", predloha `Main.dc.html` → „Smlouva · chybí tvůj podpis").
   * U zrusene a nezname faze je to jen veta „co chybí" — nazev kroku by
   * tvrdil neco, co nevime.
   */
  readonly summary: string;
  readonly steps: readonly CaseProgressStep[];
  readonly cancelled: boolean;
  /** Nazev kroku „ted" („Prověření"); `null` u zrusene a nezname faze. */
  readonly currentLabel: string | null;
}

export interface CaseProgressViewInput extends CaseProgressInput {
  /**
   * Zakazka ma smlouvu, ktera ceka na PODPIS UZIVATELE (predloha: „chybí
   * tvůj podpis"). Vi to jen volajici, ktery smlouvy nacetl — funkce si je
   * nedotahuje, aby jeden radek seznamu nezpusobil dalsi volani API.
   */
  readonly awaitingMySignature?: boolean | undefined;
}

function missingText(
  phases: readonly CasePhase[],
  currentIndex: number | null,
  cancelled: boolean,
  input: CaseProgressViewInput,
): string {
  if (cancelled) return t('webAdmin.caseProgress.cancelled', cs);
  // Krok PIL3 bod 8 (F07): „hotovo" = zadny krok uz zakazku nedrzi. Krok
  // `unavailable` (oblast, kterou REOS jeste nema) ji nedrzi — nema se v nem
  // co odbavit, jen se neodskrtava.
  if (phases.every((phase) => phase.state === 'done' || phase.state === 'unavailable')) {
    return t('webAdmin.caseProgress.allDone', cs);
  }
  if (currentIndex === null) return t('webAdmin.caseProgress.unknown', cs);
  if (input.awaitingMySignature === true) return t('webAdmin.caseProgress.missingSignature', cs);
  if (currentIndex <= LISTING_INDEX) {
    // Kroky, ktere jeste musi probehnout, NEZ je zakazka inzerovana —
    // vcetne toho, na kterem stojí ted (predloha: Prověření → „chybí 3 kroky").
    return t('webAdmin.caseProgress.missingToListing', {
      ...cs,
      values: { count: LISTING_INDEX - currentIndex + 1 },
    });
  }
  return t('webAdmin.caseProgress.missingToClose', {
    ...cs,
    values: { count: CASE_PHASE_IDS.length - currentIndex + 1 },
  });
}

/**
 * Postup zakazky pro vykresleni. `stage` je `cases.stage` (hodnota stavoveho
 * automatu) — stejny vstup, jaky bere `caseProgress`.
 */
export function caseProgressView(
  stage: string | null,
  input: CaseProgressViewInput = {},
): CaseProgressView {
  const progress = caseProgress(stage, input);
  /*
   * Krok PIL3 bod 8 (F07): procenta meri POSTUP ZAKAZKY FAZEMI — zakazka ve
   * fazi „Smlouva a předání" ctyrmi kroky opravdu prosla. Znacka „✓" tvrdi
   * neco jineho („oblast je v REOSu odbavena"), a prave ta se u oblasti
   * `unavailable` nekresli. Proto do procent `unavailable` patri a do
   * odskrtnuti ne — jedno cislo, dve ruzne vety.
   */
  const doneCount = progress.phases.filter(
    (phase) => phase.state === 'done' || phase.state === 'unavailable',
  ).length;
  const currentShare = progress.current?.progress ?? 0;
  const percent = Math.round(((doneCount + currentShare) / progress.total) * 100);
  const missing = missingText(progress.phases, progress.currentIndex, progress.cancelled, input);
  const currentLabel = progress.current?.label ?? null;
  return {
    percent,
    percentLabel: t('webAdmin.caseProgress.percent', { ...cs, values: { percent } }),
    percentDoneLabel: t('webAdmin.caseProgress.percentDone', { ...cs, values: { percent } }),
    missing,
    summary: currentLabel === null ? missing : `${currentLabel} · ${missing}`,
    steps: progress.phases.map((phase) => ({
      id: phase.id,
      label: phase.label,
      shortLabel: t(`webAdmin.casePhases.short.${phase.id}` as never, cs),
      state: phase.state,
      // Oblast, kterou REOS jeste nema (`unavailable`), se otevrit neda —
      // krok proto zustava bez sekce a tim i bez vzhledu odkazu.
      section: phase.state === 'unavailable' ? null : casePhaseSection(phase.id),
    })),
    cancelled: progress.cancelled,
    currentLabel,
  };
}
